import React, {ReactNode} from 'react';
import {View, TouchableOpacity, StyleSheet, Platform, ViewStyle} from 'react-native';
import {DrawerActions, useNavigation} from '@react-navigation/native';
import {Header, StackNavigationProp, StackHeaderLeftProps} from '@react-navigation/stack';

// StackHeaderTitleProps removed in React Navigation v7
interface StackHeaderTitleProps {
  children?: React.ReactNode;
  allowFontScaling?: boolean;
  tintColor?: string;
  style?: any;
}

import {RootMobileTitle} from '../components/rootMobileTitle';
import {Screens} from './model';

import {useTheme} from '../theme';
import {Theme} from '../theme/interface';
import {images} from '../../assets/icons';
import {useStyles} from '../hooks/styles';
import {NoInternetNotification} from '../components/noInternetNotification';
import {CardBlockedNotification} from '../components/cardBlockedNotification';

const leftButtonStyle: ViewStyle = {
  width: 44,
  height: 44,
  alignItems: 'center',
  justifyContent: 'center',
};

const Burger = (props: {navigation: StackNavigationProp<any>}) => {
  const theme = useTheme();
  const styles = useStyles(() => ({
    button: leftButtonStyle,
  }));
  return (
    <TouchableOpacity
      testID='topbar-burger-btn-id'
      style={styles.button}
      onPress={() => props.navigation.dispatch(DrawerActions.toggleDrawer)}>
      <images.Burger height={24} width={24} color={theme.color.primaryColor} />
    </TouchableOpacity>
  );
};

const CustomHeaderBackButton = (props: StackHeaderLeftProps) => {
  const theme = useTheme();
  const styles = StyleSheet.create({
    root: {
      marginHorizontal: 0,
      ...leftButtonStyle,
    },
  });
  return (
    <TouchableOpacity {...props} style={styles.root} testID='header-content'>
      <images.Left height={24} width={24} color={theme.color.primaryColor} />
    </TouchableOpacity>
  );
};

const RightSpacer = () => {
  const styles = StyleSheet.create({
    root: {
      height: 24,
      width: 24,
    },
  });
  return <View style={styles.root} />;
};

export const createCommonOptions = (
  theme: Theme,
  title: string | string[],
  isChild: boolean,
  titleNumberOfLines?: number,
) => ({
  title: Array.isArray(title) ? title.join('\n') : title,
  header: (props: any) => (
    <View testID='header-container' style={{flexDirection: 'column', alignItems: 'stretch'}}>
      <Header {...props} />
      <NoInternetNotification />
      <CardBlockedNotification currentRouteName={props.route?.name} />
    </View>
  ),
  headerBackTitleVisible: false,
  headerTitle: (props: StackHeaderTitleProps) => (
    <RootMobileTitle
      testID='header-content'
      align={isChild ? 'left' : 'center'}
      title={(props.children as any) ?? title}
      {...props}
      numberOfLines={titleNumberOfLines}
    />
  ),
  headerTitleContainerStyle: {
    flexShrink: 1,
    flexGrow: 1,
    ...Platform.select<ViewStyle>({
      ios: isChild ? {left: -20, right: 0, width: '100%'} : {width: '100%'},
      android: isChild ? {left: 0} : {width: '100%'},
      web: {left: 0, right: 0, width: '100%'},
      default: {left: 0, right: 0, width: '100%'},
    }),
  },
  headerRight: () => <RightSpacer />,
  headerRightContainerStyle: {paddingHorizontal: 10},
  headerStyle: {
    ...theme.topBar.header,
    height: 72,
  },
  headerStatusBarHeight: 0,
});

export const createRootScreenOptions = (
  theme: Theme,
  title: string | string[],
  navigation: StackNavigationProp<any>,
) => {
  const common = createCommonOptions(theme, title, false);
  return {
    ...common,
    headerLeft: () => null,
    headerRight: () => (Platform.OS === 'web' ? <Burger navigation={navigation} /> : null),
    headerLeftContainerStyle: {paddingHorizontal: 7},
    headerTitleContainerStyle: {
      ...common.headerTitleContainerStyle,
      ...(Platform.OS === 'web' ? {paddingRight: 12} : {}),
    },
  };
};

// Top-level tab screens that are direct children of MainTabs
const TOP_LEVEL_SCREENS = [
  Screens.Main,
  Screens.News,
  Screens.Profile,
  Screens.CardServicesMenu,
  Screens.PollsMenu,
  Screens.SupportServiceTab,
  Screens.NotificationsMenu,
  Screens.QrCodeCard,
];

// Screens nested inside PollsMenu
const POLLS_SCREENS = [Screens.Polls, Screens.PollsTypeSurvey, Screens.PollsTypeVote];

// Screens nested inside SupportServiceTab
const SUPPORT_SCREENS = [Screens.SupportService, Screens.SupportServiceTopics, Screens.SupportServiceTopic];

export const createChildScreenOptions = (
  theme: Theme,
  title: string,
  _navigation: StackNavigationProp<any>, // kept for backwards compatibility, but not used
  backScreen?: Screens,
  right?: ReactNode,
  titleNumberOfLines?: number,
) => {
  // Return a function to get the screen's actual navigation
  return ({navigation}: {navigation: StackNavigationProp<any>}) => {
    const handleBackPress = () => {
      // Try goBack first - now uses the screen's actual navigation
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else if (backScreen) {
        // Check if backScreen is a top-level tab
        if (TOP_LEVEL_SCREENS.includes(backScreen)) {
          (navigation.navigate as any)(Screens.MainTabs, {screen: backScreen});
        } else if (POLLS_SCREENS.includes(backScreen)) {
          // Navigate through PollsMenu
          (navigation.navigate as any)(Screens.MainTabs, {
            screen: Screens.PollsMenu,
            params: {screen: backScreen},
          });
        } else if (SUPPORT_SCREENS.includes(backScreen)) {
          // Navigate through SupportServiceTab
          (navigation.navigate as any)(Screens.MainTabs, {
            screen: Screens.SupportServiceTab,
            params: {screen: backScreen},
          });
        } else {
          // Default: nested inside CardServicesMenu
          (navigation.navigate as any)(Screens.MainTabs, {
            screen: Screens.CardServicesMenu,
            params: {screen: backScreen},
          });
        }
      } else {
        // Fallback: navigate to MainTabs (goes to initial screen)
        (navigation.navigate as any)(Screens.MainTabs);
      }
    };

    return {
      ...createCommonOptions(theme, title, true, titleNumberOfLines),
      headerLeft: (props: StackHeaderLeftProps) => <CustomHeaderBackButton {...props} onPress={handleBackPress} />,
      // right is already a ReactNode, just return it directly
      headerRight: () => (right ? right : null),
      headerLeftContainerStyle: {paddingHorizontal: 7},
    };
  };
};

const ProfileHeaderBackButton = (props: StackHeaderLeftProps & {backScreen?: Screens}) => {
  const navigation = useNavigation();
  const theme = useTheme();

  const styles = StyleSheet.create({
    root: {
      marginHorizontal: 0,
    },
  });

  const handleBack = () => {
    if (props.backScreen === Screens.CardReissue) {
      navigation.navigate(Screens.MainTabs, {screen: Screens.CardReissue});
      return;
    }

    if (props.backScreen) {
      (navigation.navigate as any)(props.backScreen);
      return;
    }

    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      // Navigate through MainTabs for Profile screens
      (navigation.navigate as any)(Screens.MainTabs, {
        screen: Screens.Profile,
        params: {screen: props.backScreen || Screens.ProfileMenu},
      });
    }
  };

  return (
    <TouchableOpacity testID='nadleBack-btn-id' style={styles.root} onPress={handleBack}>
      <images.Left height={24} width={24} color={theme.color.primaryColor} />
    </TouchableOpacity>
  );
};

export const createProfileChildScreenOptions = (
  theme: Theme,
  title: string,
  backScreen?: Screens,
  right?: ReactNode,
) => {
  return {
    ...createCommonOptions(theme, title, true),
    headerLeft: (props: StackHeaderLeftProps) => <ProfileHeaderBackButton {...props} backScreen={backScreen} />,
    headerRight: () => (right ? right : null),
    headerLeftContainerStyle: {paddingHorizontal: 7},
  };
};
